import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { Helmet } from 'react-helmet-async';

import { listMyInvoices } from '@src/api/invoices';
import paths from '@src/paths';

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') {
    return '\u2014';
  }
  const amount = Number(value);
  if (Number.isNaN(amount)) {
    return String(value);
  }
  return amount.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });
};

const formatDate = (value) => (value ? dayjs(value).format('MMM D, YYYY') : '\u2014');

const STATUS_TABS = [
  { label: 'All', value: 'all' },
  { label: 'Outstanding', value: 'OUTSTANDING' },
  { label: 'Sent', value: 'SENT' },
  { label: 'Paid', value: 'PAID' },
  { label: 'Voided', value: 'VOID' },
];

const STATUS_CHIP_PROPS = {
  SENT: { label: 'Sent', color: 'info', variant: 'outlined' },
  OUTSTANDING: { label: 'Outstanding', color: 'warning', variant: 'filled' },
  PAID: { label: 'Paid', color: 'success', variant: 'filled' },
  VOID: { label: 'Voided', color: 'default', variant: 'outlined' },
};

const URGENCY_CONFIG = {
  critical: { label: 'Account at risk of pause', color: 'error', variant: 'filled' },
  final: { label: 'Final notice \u2014 pause imminent', color: 'error', variant: 'outlined' },
  warning: { label: 'Past due', color: 'warning', variant: 'filled' },
  overdue: { label: 'Overdue', color: 'warning', variant: 'outlined' },
};

function StatusChip({ status, urgency, daysPastDue }) {
  if (urgency && URGENCY_CONFIG[urgency]) {
    const cfg = URGENCY_CONFIG[urgency];
    const daysLabel = daysPastDue != null ? ` (${daysPastDue}d)` : '';
    return <Chip size="small" label={`${cfg.label}${daysLabel}`} color={cfg.color} variant={cfg.variant} />;
  }
  const props = STATUS_CHIP_PROPS[status] || { label: status || '\u2014', color: 'default', variant: 'outlined' };
  return <Chip size="small" {...props} />;
}

export default function ClientInvoiceListPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchInvoices = useCallback((statusValue) => {
    setLoading(true);
    setError('');
    const params = { ordering: '-issue_date', page_size: 100 };
    if (statusValue && statusValue !== 'all') {
      params.status = statusValue;
    }
    listMyInvoices(params)
      .then((res) => {
        const results = res?.data?.results || res?.data || [];
        setInvoices(Array.isArray(results) ? results : []);
      })
      .catch((err) => {
        console.error('Unable to load invoices', err);
        const detail = err?.response?.data?.detail;
        setError(detail || 'Unable to load invoices.');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchInvoices(statusFilter);
  }, [fetchInvoices, statusFilter]);

  const handleTabChange = (event, newValue) => {
    setStatusFilter(newValue);
  };

  const handleRowClick = (invoice) => {
    navigate(paths.myInvoices.details(invoice.number));
  };

  const summaryStats = useMemo(() => {
    let outstandingCount = 0;
    let outstandingTotal = 0;
    let paidCount = 0;
    let paidTotal = 0;
    invoices.forEach((inv) => {
      if (inv.status === 'OUTSTANDING' || inv.status === 'SENT') {
        outstandingCount += 1;
        outstandingTotal += parseFloat(inv.balance_due || inv.total_amount || 0);
      }
      if (inv.status === 'PAID') {
        paidCount += 1;
        paidTotal += parseFloat(inv.total_amount || 0);
      }
    });
    return { outstandingCount, outstandingTotal, paidCount, paidTotal };
  }, [invoices]);

  const criticalInvoices = useMemo(
    () => invoices.filter((inv) => inv.urgency === 'critical' || inv.urgency === 'final'),
    [invoices],
  );
  const warningInvoices = useMemo(
    () => invoices.filter((inv) => inv.urgency === 'warning' || inv.urgency === 'overdue'),
    [invoices],
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Helmet>
        <title>Invoices &middot; Lead Gurus</title>
      </Helmet>

      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Invoices
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            View and pay your invoices
          </Typography>
        </Box>

        {criticalInvoices.length > 0 && (
          <Alert severity="error" variant="filled" sx={{ borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Your account is at risk of being paused
            </Typography>
            <Typography variant="body2">
              {criticalInvoices.length === 1
                ? `Invoice ${criticalInvoices[0].number} is ${criticalInvoices[0].days_past_due}+ days past due. `
                : `${criticalInvoices.length} invoices are 5+ days past due. `}
              Lead delivery will be paused until payment is received. Please pay immediately to avoid disruption.
            </Typography>
          </Alert>
        )}

        {warningInvoices.length > 0 && criticalInvoices.length === 0 && (
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              You have past-due invoices
            </Typography>
            <Typography variant="body2">
              {warningInvoices.length === 1
                ? `Invoice ${warningInvoices[0].number} is ${warningInvoices[0].days_past_due} day${warningInvoices[0].days_past_due !== 1 ? 's' : ''} past due. `
                : `${warningInvoices.length} invoices are past due. `}
              Accounts are paused at 6 days overdue. Please submit payment to keep your lead flow running.
            </Typography>
          </Alert>
        )}

        {!loading && !error && invoices.length > 0 && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Card variant="outlined" sx={{ borderRadius: 2, flex: 1, borderLeft: '4px solid', borderLeftColor: summaryStats.outstandingCount > 0 ? 'warning.main' : 'divider' }}>
              <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  Outstanding
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: summaryStats.outstandingCount > 0 ? 'warning.main' : 'text.primary' }}>
                  {formatCurrency(summaryStats.outstandingTotal)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {summaryStats.outstandingCount} invoice{summaryStats.outstandingCount !== 1 ? 's' : ''} due
                </Typography>
              </CardContent>
            </Card>
            <Card variant="outlined" sx={{ borderRadius: 2, flex: 1, borderLeft: '4px solid', borderLeftColor: 'success.main' }}>
              <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  Paid
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>
                  {formatCurrency(summaryStats.paidTotal)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {summaryStats.paidCount} invoice{summaryStats.paidCount !== 1 ? 's' : ''}
                </Typography>
              </CardContent>
            </Card>
            <Card variant="outlined" sx={{ borderRadius: 2, flex: 1 }}>
              <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  Total invoices
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {invoices.length}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  All time
                </Typography>
              </CardContent>
            </Card>
          </Stack>
        )}

        <Tabs value={statusFilter} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          {STATUS_TABS.map((tab) => (
            <Tab key={tab.value} label={tab.label} value={tab.value} />
          ))}
        </Tabs>

        {loading ? (
          <Box display="flex" justifyContent="center" sx={{ py: 8 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {error}
          </Alert>
        ) : invoices.length === 0 ? (
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <Typography color="text.secondary">
                {statusFilter === 'all'
                  ? 'No invoices found.'
                  : `No ${STATUS_TABS.find((t) => t.value === statusFilter)?.label?.toLowerCase() || ''} invoices.`}
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Invoice</TableCell>
                    <TableCell>Period</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Balance Due</TableCell>
                    <TableCell>Due Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.map((invoice) => {
                    const balanceDue = parseFloat(invoice.balance_due || 0);
                    const isOverdue =
                      invoice.status === 'OUTSTANDING' &&
                      invoice.due_date &&
                      dayjs(invoice.due_date).isBefore(dayjs(), 'day');

                    return (
                      <TableRow
                        key={invoice.id || invoice.number}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleRowClick(invoice)}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {invoice.number}
                          </Typography>
                          {invoice.client_name ? (
                            <Typography variant="caption" color="text.secondary">
                              {invoice.client_name}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {invoice.period_label || `${formatDate(invoice.period_start)} \u2013 ${formatDate(invoice.period_end)}`}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <StatusChip status={invoice.status} urgency={invoice.urgency} daysPastDue={invoice.days_past_due} />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {formatCurrency(invoice.total_amount)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: balanceDue > 0 ? 600 : 400,
                              color: isOverdue ? 'error.main' : balanceDue > 0 ? 'warning.main' : 'text.secondary',
                            }}
                          >
                            {formatCurrency(invoice.balance_due)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            color={isOverdue ? 'error.main' : 'text.primary'}
                          >
                            {formatDate(invoice.due_date)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        )}
      </Stack>
    </Container>
  );
}
